import { Typography } from '@mui/joy';
import {
  ArrowsOutCardinal,
  FolderDashed,
  Folder as FolderIcon,
} from '@phosphor-icons/react';
import _ from 'lodash';
import { MouseEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DirectoryPathProps } from '../../TabDisplay';
import { findItemAndParent } from '../../utils/utils';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Text } from '~/components/ui/text';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ReportService } from '~/services/api/reports/endpoints';
import {
  AttachmentFolder,
  AttachmentFile,
  ReportAttachementProps,
} from '~/services/api/reports/types';
import { isMutationSuccess } from '~/services/api/rest-api';
import { useCurrentUser } from '~/services/api/users/endpoints';

type FileOrFolder = AttachmentFile | AttachmentFolder;

interface MoveFolderModalProps {
  checkedItems: ReportAttachementProps[];
  attachments: AttachmentFolder;
  reportId?: string | undefined;
  attachmentsDisplay?: FileOrFolder[];
  directoryPath?: DirectoryPathProps[] | undefined;
  displayAsPopover?: boolean;
}

const MoveFolderModal = ({
  displayAsPopover = false,
  checkedItems,
  attachments,
  reportId,
  attachmentsDisplay,
  directoryPath,
}: MoveFolderModalProps) => {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const user = useCurrentUser();
  const [attachmentsDisplayCopy, setAttachmentsDisplayCopy] =
    useState(attachmentsDisplay);
  const [directoryPathCopy, setDirectoryPathCopy] = useState(directoryPath);

  useEffect(() => {
    if (directoryPathCopy !== undefined) {
      const currentFolderId =
        directoryPathCopy[directoryPathCopy.length - 1].id;
      const addDirectory = findItemAndParent(attachments, currentFolderId);
      if (addDirectory.item) {
        setAttachmentsDisplayCopy(
          addDirectory.item.items
            .map((rest) => ({
              ...rest,
              items: [],
            }))
            .filter((obj) => !checkedItems.some((item) => item.id === obj.id))
            .filter((obj) => obj.is_folder), // only folders are allowed as destinations
        );
      }
    }
  }, [directoryPathCopy, attachmentsDisplayCopy]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (folderId: number | null) => {
    const index = directoryPathCopy?.findIndex((i) => i.id === folderId);

    if (index !== undefined && index !== -1) {
      setDirectoryPathCopy(directoryPathCopy?.slice(0, index + 1));
    }
  };

  const handleNavigateFolder = (folderId: number | null) => {
    const findItem = findItemAndParent(attachments, folderId);

    if (findItem.item) {
      const newItem: DirectoryPathProps = {
        id: findItem.item.id,
        name: findItem.item.name,
      };

      setDirectoryPathCopy((prev) => [...(prev || []), newItem]);
    }
  };

  const [moveFolder] =
    ReportService.endpoints.renameAndMoveFolderCasemanager.useMutation();
  const [moveFile] = ReportService.endpoints.moveFileCasemanager.useMutation();

  const handleMove = () => {
    checkedItems.forEach(async (attachment) => {
      const destinationFolderId = directoryPathCopy?.slice(-1)[0]?.id;
      const defaultPath = directoryPath?.slice(-1)[0]?.id;

      if (!reportId || !user.data?.id) return;

      if (destinationFolderId === defaultPath) {
        return createToastNotification(
          intl.formatMessage({
            defaultMessage: 'Invalid destination folder.',
          }),
          'error',
        );
      }

      if (attachment.is_folder) {
        const result = await moveFolder({
          folder_id: attachment.id,
          name: attachment.name,
          uploaderID: user.data?.id,
          report: reportId,
          parent_folder: destinationFolderId,
        });

        if (!isMutationSuccess(result)) {
          return createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Something went wrong.',
            }),
            'error',
          );
        }
      } else {
        if (destinationFolderId !== null && destinationFolderId !== undefined) {
          const result = await moveFile({
            parent_folder: 2,
            file_id: attachment.id,
            distination_folder: destinationFolderId,
          });

          if (!isMutationSuccess(result)) {
            return createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Something went wrong.',
              }),
              'error',
            );
          }
        } else {
          return createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Invalid destination folder.',
            }),
            'error',
          );
        }
      }
    });

    return createToastNotification(
      intl.formatMessage({
        defaultMessage: 'Successfully moved.',
      }),
      'success',
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {displayAsPopover ? (
          <Button
            variant='soft'
            color='neutral'
            className='block !justify-start w-full !rounded-none px-4 py-2 text-gray-800 hover:bg-gray-200'
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
            }}
            startDecorator={<ArrowsOutCardinal size={20} color='#434c4c' />}
          >
            <FormattedMessage defaultMessage={'Move'} />
          </Button>
        ) : (
          <Button
            className='flex items-center w-3/10 !rounded-full'
            variant='outlined'
            color='neutral'
            startDecorator={<ArrowsOutCardinal size={20} color='#434c4c' />}
          >
            <FormattedMessage defaultMessage='Move' />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent size='lg' className='mx-auto w-full h-50 relative'>
        <div className='text-lg font-bold mb-7'>
          <FormattedMessage defaultMessage='Move item to...' />
        </div>

        {/* Folder Path directory */}
        <div className='flex items-center w-full'>
          <div className='flex items-center'>
            {directoryPathCopy?.map((folder, index) => (
              <div key={index} className='flex flex-row'>
                {index > 0 && (
                  <Typography className='!text-[#878787] font-schibsted text-base font-normal leading-[1.25] px-2'>
                    /
                  </Typography>
                )}
                <div className='flex items-center'>
                  <Typography
                    className=' font-schibsted text-base font-normal leading-[1.25] cursor-pointer'
                    onClick={(event: MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      handleNavigate(folder.id);
                    }}
                  >
                    {folder.name}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='list-item gap-1 overflow-hidden mt-2 mb-4'>
          {attachmentsDisplayCopy?.map(
            (i) =>
              i.is_folder ? (
                <button
                  key={i.id}
                  className='relative  flex items-center p-2 text-left overflow-hidden shadow-sm w-full hover:bg-gray-100 cursor-pointer group'
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    handleNavigateFolder(i.id);
                  }}
                >
                  <div className='w-full flex items-center justify-between'>
                    <div className='flex items-center'>
                      <FolderIcon
                        size={40}
                        color='#00cecf'
                        weight='fill'
                        className='mr-4'
                      />
                      <Text className='text-md text-gray-500'>{i.name}</Text>
                    </div>
                  </div>
                </button>
              ) : undefined, // only folder is allowed to move
          )}

          {/* Display if no Folder */}
          {attachmentsDisplayCopy?.length === 0 && (
            <div className='flex flex-col justify-center items-center mt-3 mb-3 gap-4'>
              <FolderDashed size={68} color='#666666' />
              <Text className='text-neutral-500'>
                <FormattedMessage defaultMessage='No folders found.' />
              </Text>
            </div>
          )}
        </div>

        <div className='flex justify-end items-end space-x-4'>
          <DialogClose asChild>
            <Button
              size='md'
              variant='outlined'
              color='neutral'
              className='text-base'
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                setDirectoryPathCopy(directoryPath);
                event.stopPropagation();
              }}
            >
              Cancel
            </Button>
          </DialogClose>

          <Button
            size='md'
            className='bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-full'
            type='submit'
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              handleMove();
            }}
            disabled={_.isEqual(directoryPath, directoryPathCopy)}
          >
            <FormattedMessage defaultMessage='Move' />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoveFolderModal;
